import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';
import { 
  Menu, X, LogOut, Settings, ChevronDown, Search, Newspaper,
  Home, Bot, GraduationCap, Info, Image, Star, HelpCircle, BookOpen, Phone, Lock
} from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth(); // 'user' now represents an authenticated Admin
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const navLinks = [
    { path: '/', label: t('nav.home'), icon: Home },
    { path: '/dauter-ai', label: t('nav.dauterAi'), icon: Bot },
    { path: '/classes', label: t('nav.classes'), icon: GraduationCap },
    { path: '/search', label: t('nav.search'), icon: Search },
    { path: '/news', label: t('nav.news'), icon: Newspaper },
    { path: '/about', label: t('nav.about'), icon: Info },
    { path: '/gallery', label: t('nav.gallery'), icon: Image },
    { path: '/reviews', label: t('nav.reviews'), icon: Star },
    { path: '/faq', label: t('nav.faq'), icon: HelpCircle },
    { path: '/blog', label: t('nav.blog'), icon: BookOpen },
    { path: '/contact', label: t('nav.contact'), icon: Phone },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1" data-testid="logo">
            <span className="logo-sp text-2xl text-white">SP</span>
            <span className="logo-sp text-2xl logo-dot">.</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector />
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-zinc-300 hover:text-white"
                    data-testid="admin-menu-trigger"
                  >
                    <Settings className="w-5 h-5 text-red-500" />
                    <span className="max-w-[100px] truncate">Admin</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#121212] border-zinc-800">
                  <DropdownMenuItem
                    onClick={() => navigate('/admin')}
                    className="cursor-pointer"
                    data-testid="admin-link"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t('nav.adminPanel', 'Admin Panel')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-red-400"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout', 'Logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              /* Subtle Admin Login Link for Guests */
              <Link 
                to="/login" 
                className="p-2 ml-2 text-zinc-800 hover:text-zinc-500 transition-colors"
                title="Admin Access"
              >
                <Lock className="w-4 h-4" />
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSelector />
            <button
              className="p-2 text-zinc-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden mobile-menu border-t border-zinc-800 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 text-base font-medium rounded-sm transition-colors ${
                  isActive(link.path)
                    ? 'text-white bg-zinc-800'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {link.icon && <link.icon className="w-4 h-4" />}
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-zinc-800">
              {user ? (
                <>
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-base font-medium text-zinc-400 hover:text-white"
                  >
                    <Settings className="inline w-4 h-4 mr-2" />
                    {t('nav.adminPanel', 'Admin Panel')}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-red-400 hover:text-red-300"
                  >
                    <LogOut className="inline w-4 h-4 mr-2" />
                    {t('nav.logout', 'Logout')}
                  </button>
                </>
              ) : (
                /* Subtle Mobile Admin Link */
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-zinc-800 hover:text-zinc-600"
                >
                  Admin Access
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
